<template>
    <div :style="{ maxWidth: getChartWidth }">
        <div v-if="!this.isDataLoading">
            <Bar
                :chart-options="chartOptions"
                :chart-data="chartData"
                :chart-id="chartId"
                :dataset-id-key="datasetIdKey"
                :plugins="plugins"
                :css-classes="cssClasses"
                :width="width"
                :height="height"
                class="mixed-chart"
                ref="chartComponent"
            />
            <p class="text-center measure-type-footer">{{ graphFooterText }}</p>
        </div>
        <div v-else :style="`height: ${height / 2}px !important;`" class="d-flex justify-content-center align-items-center h-100">
            <Spinner class="mt-5 pt-5 custom-spinner" loadingMsg="Loading bar chart..." />
        </div>
    </div>
</template>

<script>
import { Bar } from "vue-chartjs/legacy"
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LineElement, LinearScale, PointElement, LineController } from "chart.js"
import * as Helpers from "@/helpers"
import Spinner from "@/components/Shared/Spinner/Spinner"

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LineElement, PointElement, LinearScale, LineController)

export default {
    components: { Bar, Spinner },
    props: {
        chartId: {
            type: String,
            default: "bar-chart",
        },
        graphText: {
            type: String,
        },
        datasetIdKey: {
            type: String,
            default: "label",
        },
        width: {
            type: Number,
            default: 400,
        },
        height: {
            type: Number,
            default: 335,
        },

        cssClasses: {
            default: "",
            type: String,
        },
        styles: {
            type: Object,
            default: () => {},
        },
        plugins: {
            type: Array,
            default: () => [],
        },
        graphItems: {
            type: Array,
            // default: () => [],
        },
        measureTypeSelected: {
            type: String,
        },
    },
    data() {
        return {
            measureSumModeKey: "DAY",
            measureSumModeProperty: "date",
        }
    },
    watch: {
        graphText(newGraphText) {
            this.chartOptions.plugins.title.text = newGraphText
        },
        "$store.getters.getAdsCampaignReportMeasureSumMode.key"(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.measureSumModeKey = newVal
                this.measureSumModeProperty = this.getMeasureSumPropertyName()
            }
        },
    },
    computed: {
        chartOptions() {
            let thisComponent = this;
            return {
                locale: "sv",
                responsive: true,
                maintainAspectRatio: false,

                scales: {
                    left: {
                        stacked: true,
                        id: "left",
                        position: "left",
                        grid: {
                            display: true,
                            // Show horizontal gridline only for every other tick
                            color: function(context) {
                                return context.index % 2 == 0 ? "#E5E5E5" : "transparent"
                            },
                        },
                        ticks: {
                            callback: function(value, index) {
                                // Only display even index ticks
                                return index % 2 === 0 ? value : ""
                            },
                        },
                    },
                    x: {
                        stacked: true,
                        grid: {
                            display: false,
                        },
                        ticks: {
                            color: function(value) {
                                let onlineProvider = thisComponent.sortedGraphItems.find(item => item[thisComponent.measureSumModeProperty] === value.tick.label).onlineProvider
                                return onlineProvider ? "#337ab7" : "grey"
                            },
                        },
                    },
                },
                plugins: {
                    title: {
                        text: this.graphText, // Set the title text to the graphText prop
                        display: true,
                        font: {
                            size: 16,
                        },
                    },
                    legend: {
                        display: false,
                        position: "right",
                        labels: {
                            boxHeight: 5,
                            boxWidth: 40,
                            padding: -30,
                            backgroundColor: "white",
                        },
                    },
                    tooltip: {
                        callbacks: {
                            label: function(tooltipItem) {
                                return `${tooltipItem.raw.toFixed(1)}`
                            },
                        },
                    },
                    // required to avoid memory stack full error
                    annotation: {
                        annotations: [],
                    },
                },
            }
        },
        graphFooterText() {
            const reachText = this.$store.getters.getAdsCampaignReportMeasureTypeSelected === "absolute_reach" ? "Reach 000s" : "Reach %"
            return `*${reachText} ${this.$store.getters.getAdsCampaignReportCurrentTargetGroup}  (TV: 1min, OV: 5s or 50% of length)`
        },
        graphItemsCount() {
            return this.chartData?.datasets[0]?.data?.length
        },
        graphMeasures() {
            return this.$store.getters.getAdsCampaignReportResults.data.measureColumns.filter(
                m => m.subHeader.includes(this.$store.getters.getAdsCampaignReportCurrentTargetGroup) && m.header.includes("Reach") && m.subHeader.toLowerCase().includes("Total".toLowerCase())
            )
        },
        chartData() {
            if (!this.isDataLoading && this.sortedGraphItems && this.sortedGraphItems.length) {
                return {
                    labels: this.labels,
                    datasets: [
                        {
                            type: "bar",
                            yAxisID: "left",
                            backgroundColor: "#337AB7",
                            data: this.sortedGraphItems.map(item => item.sumTotal),
                            order: 1,
                        },
                    ],
                }
            } else {
                return { labels: [""], datasets: [{}] }
            }
        },
        getChartWidth() {
            const baseWidth = 25
            let width = this.sortedGraphItems.length * 0.25 * baseWidth
            width = Math.max(width, 25)
            return `${width}rem`
        },
        isDataLoading() {
            return this.$store.getters.getAdsCampaignReportResults.status === "LOADING" || this.$store.getters.getAdsCampaignReportResults.data[this.measureSumModeKey].status === "NOT_LOADED"
        },
        selectedMeasureSumMode() {
            return this.$store.getters.getAdsCampaignReportMeasureSumMode.label
        },
        sortedGraphItems() {
            if (this.$store.getters.getAdsCampaignReportResults.data[this.measureSumModeKey].status === "LOADED") {
                let rawData = this.$store.getters.getAdsCampaignReportResults.data[this.measureSumModeKey].levelOneAdsWithLevelTwoAds
                let items = rawData.map(item => {
                    let totalSum = "0"

                    // Loop through each graph measure and sum up
                    this.graphMeasures.forEach(measure => {
                        const measureValue = item[measure.key]
                        if (measureValue !== " ") {
                            if (measure.subHeader.toLowerCase().includes("Total".toLowerCase())) {
                                totalSum = Helpers.addStringsAsNumbersSafe(totalSum, measureValue)
                            }
                        }
                    })
                    return {
                        [this.measureSumModeProperty]: item[this.measureSumModeProperty],
                        onlineProvider: item.providerGroup === "Sites",
                        sumTotal: parseFloat(totalSum.replace(",", ".")),
                    }
                })

                // Sort items by date if sum mode = DAY
                if (this.measureSumModeKey === "DAY") {
                    items.sort((a, b) => new Date(a[this.measureSumModeProperty]) - new Date(b[this.measureSumModeProperty]))
                } else if (this.measureSumModeKey === "WEEK") {
                    items.sort((a, b) => {
                        return a[this.measureSumModeProperty].localeCompare(b[this.measureSumModeProperty])
                    })
                } else {
                    items.sort((a, b) => {
                        return b.sumTotal - a.sumTotal
                    })
                }

                return items
            } else {
                return []
            }
        },
        labels() {
            let labelStrings = this.sortedGraphItems.map(item => item[this.measureSumModeProperty])
            return labelStrings
        },
        rows() {
            return this.$store.getters.getAdsCampaignReportResults.data[this.measureSumModeKey].levelOneAdsWithLevelTwoAds
        },
    },
    methods: {
        getMeasureSumPropertyName() {
            switch (this.measureSumModeKey) {
                case "PROVIDER":
                    return "providerName"
                case "FILMCODE":
                    return "filmcode"
                case "WEEK":
                    return "yearWeek"
                case "DAY":
                    return "date"
                default:
                    return "date"
            }
        },
    },
}
</script>

<style scoped>
.measure-type-footer {
    color: gray;
    font-style: italic;
    margin-top: 1rem;
    padding-right: 0rem;
}
.custom-spinner {
    font-size: 1rem;
}
</style>
